import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, CurrencyGbp, Star, Briefcase, Warning } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import TrustBadge from "@/components/TrustBadge";
import { devWarn } from "@/lib/logger";

export default function StaffDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recommended, setRecommended] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, j] = await Promise.all([api.get("/staff/dashboard"), api.get("/matching/recommended-jobs")]);
        setStats(s.data);
        setRecommended(j.data.slice(0, 6));
      } catch (err) { devWarn("dashboard load failed", err); }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Welcome back</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Hi, {user?.first_name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {user?.id && <TrustBadge staffId={user.id} compact />}
          <StatusBadge status={stats?.account_status || user?.status} />
        </div>
      </div>

      {user?.status === "pending_approval" && (
        <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <Warning size={20} className="text-yellow-700 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900">Approval pending</div>
            <div className="text-sm text-slate-700">Complete your profile and upload documents. Once an admin approves you'll be able to book shifts.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Calendar} label="Upcoming" value={stats?.upcoming_count ?? 0} />
        <StatCard icon={Briefcase} label="Completed" value={stats?.completed_count ?? 0} />
        <StatCard icon={CurrencyGbp} label="Earnings" value={`£${stats?.total_earnings ?? 0}`} />
        <StatCard icon={Star} label="Rating" value={stats?.rating || "—"} />
      </div>

      {stats?.next_shift && (
        <div className="card-tactile p-6">
          <div className="label-caps text-blue-600 mb-2">Next shift</div>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display text-2xl font-bold text-slate-900">{stats.next_shift.job?.title}</h3>
              <div className="text-slate-600 mt-1">{stats.next_shift.job?.company_name}</div>
              <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-700">
                <span className="flex items-center gap-1.5"><Calendar size={14}/> {stats.next_shift.job_date}</span>
                <span className="flex items-center gap-1.5"><Clock size={14}/> {stats.next_shift.start_time}–{stats.next_shift.end_time}</span>
                <span className="flex items-center gap-1.5"><MapPin size={14}/> {stats.next_shift.job?.location}</span>
              </div>
            </div>
            <StatusBadge status={stats.next_shift.status} />
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl font-extrabold text-slate-900">Recommended jobs</h2>
          <Link to="/staff/jobs" className="text-blue-600 font-semibold text-sm">See all →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recommended.map((j) => (
            <Link key={j.id} to={`/staff/jobs/${j.id}`} data-testid={`rec-job-${j.id}`} className="card-tactile p-5">
              <div className="label-caps text-blue-600 mb-1">{j.category}</div>
              <div className="font-display text-lg font-bold text-slate-900">{j.title}</div>
              <div className="text-sm text-slate-600">{j.company_name}</div>
              <div className="mt-3 text-sm text-slate-700 space-y-1">
                <div className="flex items-center gap-1.5"><MapPin size={14}/> {j.location}</div>
                <div className="flex items-center gap-1.5"><Calendar size={14}/> {j.date} · {j.start_time}–{j.end_time}</div>
              </div>
              <div className="mt-3 font-display text-xl font-extrabold text-slate-900">£{j.pay_rate}/hr</div>
            </Link>
          ))}
          {recommended.length === 0 && <div className="text-slate-500">No jobs available yet — check back soon.</div>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Quick to="/staff/jobs" label="Find Jobs" />
        <Quick to="/staff/bookings" label="My Bookings" />
        <Quick to="/staff/availability" label="Availability" />
        <Quick to="/staff/documents" label="Documents" />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-tactile p-4">
      <Icon size={20} className="text-blue-600 mb-2" />
      <div className="label-caps">{label}</div>
      <div className="font-display text-2xl font-extrabold text-slate-900">{value}</div>
    </div>
  );
}
function Quick({ to, label }) {
  return (
    <Link to={to} className="p-4 bg-white border border-slate-200 rounded-md text-center font-semibold text-slate-800 hover:bg-slate-50">
      {label}
    </Link>
  );
}
